/**
 * Jeopardy-style tile flip animation (pulse -> rotate -> zoom).
 *
 * Split out of board.ts so the scene file stays under the 500-line cap
 * the code-quality hook enforces. Returns a factory that holds per-flip
 * state and exposes a minimal tick/start API the board's tick loop calls
 * every frame.
 *
 * @packageDocumentation
 */

import { clamp01, easeOutCubic } from './lib/easing.js';

/** Duration breakdown for the three animation phases (ms). */
export const FLIP_PULSE_MS = 260;
export const FLIP_ROTATE_MS = 520;
export const FLIP_ZOOM_MS = 380;
export const FLIP_TOTAL_MS = FLIP_PULSE_MS + FLIP_ROTATE_MS + FLIP_ZOOM_MS;

/** Per-frame transform values the board applies to the flipping tile. */
export interface FlipFrame {
  /** Uniform scale to apply to the tile + overlay. Ranges 1 -> ~1.5. */
  scale: number;
  /** Y-axis rotation (radians). 0 -> PI across the rotate phase. */
  rotY: number;
  /** Overlay text opacity (fades on the back half of the flip). */
  textOpacity: number;
  /** True once the zoom phase has elapsed; caller should end the flip. */
  done: boolean;
}

/**
 * Compute the transform values for a given elapsed time. Pure; exported
 * so unit tests can drive the animation frame-by-frame.
 */
export function flipFrameAt(elapsed: number): FlipFrame {
  let scale = 1;
  let rotY = 0;
  if (elapsed < FLIP_PULSE_MS) {
    const t = clamp01(elapsed / FLIP_PULSE_MS);
    scale = 1 + 0.08 * Math.sin(t * Math.PI);
  } else if (elapsed < FLIP_PULSE_MS + FLIP_ROTATE_MS) {
    const t = clamp01((elapsed - FLIP_PULSE_MS) / FLIP_ROTATE_MS);
    const e = easeOutCubic(t);
    rotY = Math.PI * e;
    scale = 1 + 0.05 * Math.sin(t * Math.PI);
  } else {
    const t = clamp01((elapsed - FLIP_PULSE_MS - FLIP_ROTATE_MS) / FLIP_ZOOM_MS);
    const e = easeOutCubic(t);
    rotY = Math.PI;
    scale = 1 + 0.5 * e;
  }
  const textOpacity = clamp01(Math.abs(Math.cos(rotY)));
  return { scale, rotY, textOpacity, done: elapsed >= FLIP_TOTAL_MS };
}
